import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { statusKind } from '../../atoms/statusKind.ts'
import { staleValue } from '../format.ts'

import { holdKindOf } from './monitoringModel.ts'

export interface ModuleSummaryChip {
  label: string
  status: string
  /** 칩 글자와 서버 원값이 다를 때 툴팁에 싣는다(보류 kind 등) */
  originalValue?: string | undefined
}

/**
 * 작업 중 분류(`06 §4.2a`)지만 **가동부가 멈춰 있는** 상태 — 「실행 중」으로 접지 않고 원값 문구로 세운다.
 * `INTERRUPTED` = 코어 자동 취소로 그 자리에 멈춰 이어 보내기를 기다린다(`SM-MOD-23` ⓑ).
 * 분류(사람 할 일 없음)는 정본대로 두고, 칩 글자만 「돈다」고 말하지 않게 한다.
 */
const HALTED_RUN_STATES: ReadonlySet<string> = new Set(['INTERRUPTED'])

/**
 * 모듈 앱 헤더 · 메트릭 칩의 요약 표기.
 *
 * 🔴 **두 값(「실행 중」 · 「대기」)으로 접는 것은 정상 계열뿐이다** — 작업 중 계열(`statusKind` = run)은
 *    「실행 중」, 원값 `IDLE` 은 「대기」. 그 밖(오류 · 안전정지 · 복구 중 · 개입 대기 · 사전에 없는 값)은
 *    **서버 원값 그대로**(`enum.moduleState`) 칩이 선다. 이상 상태를 「대기」로 접지 않는다(2026-10-26 · 9a4f8ef 회귀).
 *    ⚠ `statusKind` 는 모르는 값을 `idle` 모양으로 떨어뜨린다 — 그래서 「대기」 판정은 원값 `IDLE` 하나로만 한다.
 * 🔴 **보류(`hold`)가 서 있으면 상태보다 보류를 말한다** — `state: RUNNING` 인 채 멈춘 모듈을 「실행 중」이라 하지 않는다.
 *    얼굴은 `HoldBadge`(MonitoringScreen)와 같은 일시정지(`PAUSED`)다.
 * 🔴 **모르면 칩이 안 선다** — 스냅샷 미수신 · 상태 빈 값은 `undefined`. 「대기」로 채우지 않는다.
 * 🔴 낡은 스냅샷은 값 뒤에 「마지막 값」 표지(`staleValue`) — 마지막 값을 지금 값으로 보이지 않는다.
 */
export function moduleSummaryState(
  snapshot: ModuleSnapshot | undefined,
): ModuleSummaryChip | undefined {
  if (snapshot === undefined) return undefined
  // 계약은 string 이지만 캐스트로 빈 값 · null 이 들어온다(holdKindOf 와 같은 방어)
  const state: string | null | undefined = snapshot.state
  if (state == null || state === '') return undefined
  const hold = holdKindOf(snapshot)
  const chip: ModuleSummaryChip =
    hold !== undefined
      ? {
          label: t('monitoring.hold', { kind: enumLabel('enum.holdKind', hold) }),
          status: 'PAUSED',
          originalValue: hold,
        }
      : statusKind(state) === 'run' && !HALTED_RUN_STATES.has(state)
        ? {
            label: t('enum.moduleState.RUNNING'),
            status: 'RUNNING',
            originalValue: state === 'RUNNING' ? undefined : state,
          }
        : state === 'IDLE'
          ? { label: t('enum.moduleState.IDLE'), status: 'IDLE' }
          : { label: enumLabel('enum.moduleState', state), status: state }
  return { ...chip, label: staleValue(chip.label, snapshot.stale === true) }
}
